import { useCallback, useEffect, useRef } from "react"

import { VideoReviewStatus } from "../pages/VideoReviews/VideoReviewStatus"

export const PROCESSING_POLL_INTERVAL_MS = 10_000

type VideoReviewRow = {
  id: number
  status?: string
  video_url?: string | null
  videoUrl?: string | null
  processed_at?: string | null
  processedAt?: string | null
  processing_time?: number | null
  processingTime?: number | null
  status_description?: string | null
  statusDescription?: string | null
  [key: string]: unknown
}

type JobStatusResponse = {
  success: boolean
  data?: {
    video_review_status?: string
  }
  error?: string
}

type VideoStatusResponse = {
  success: boolean
  data?: {
    status?: string
    status_description?: string
    video_url?: string | null
    processed_at?: string | null
    processing_time?: number | null
  }
}

const ACTIVE_PROCESSING_STATUSES: string[] = [VideoReviewStatus.PROCESSING, VideoReviewStatus.UPLOADING]

export async function syncVideoReviewJobStatus(videoId: number): Promise<string | null> {
  const response = await fetch(`/api/videos/${videoId}/job_status`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include"
  })

  const data: JobStatusResponse = await response.json()

  if (!response.ok || !data.success) {
    return null
  }

  return data.data?.video_review_status ?? null
}

async function fetchVideoReviewDetails(videoId: number): Promise<VideoStatusResponse["data"] | null> {
  const response = await fetch(`/api/videos/${videoId}/status`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include"
  })

  const data: VideoStatusResponse = await response.json()

  if (!response.ok || !data.success || !data.data) {
    return null
  }

  return data.data
}

function mergeStatusIntoRow(video: VideoReviewRow, details: NonNullable<VideoStatusResponse["data"]>): VideoReviewRow {
  return {
    ...video,
    status: details.status,
    status_description: details.status_description,
    statusDescription: details.status_description,
    video_url: details.video_url,
    videoUrl: details.video_url,
    processed_at: details.processed_at,
    processedAt: details.processed_at,
    processing_time: details.processing_time,
    processingTime: details.processing_time
  }
}

/**
 * Polls MediaConvert job status for in-flight video reviews and updates portal list state
 * when transcoding completes (mirrors the client upload page poll, without a full reload).
 */
export function useProcessingVideoReviewsPoll(
  videoReviews: VideoReviewRow[],
  setVideoReviews: React.Dispatch<React.SetStateAction<VideoReviewRow[]>>
): { syncProcessingVideo: (videoId: number) => Promise<boolean> } {
  const videoReviewsRef = useRef(videoReviews)
  videoReviewsRef.current = videoReviews

  const syncProcessingVideo = useCallback(
    async (videoId: number): Promise<boolean> => {
      const current = videoReviewsRef.current.find((v) => v.id === videoId)
      if (!current) return false

      const nextStatus = await syncVideoReviewJobStatus(videoId)
      if (!nextStatus || nextStatus === current.status) {
        return false
      }

      const details = await fetchVideoReviewDetails(videoId)
      if (!details) {
        setVideoReviews((prev) => prev.map((v) => (v.id === videoId ? { ...v, status: nextStatus } : v)))
        return true
      }

      setVideoReviews((prev) => prev.map((v) => (v.id === videoId ? mergeStatusIntoRow(v, details) : v)))
      return true
    },
    [setVideoReviews]
  )

  useEffect(() => {
    const poll = () => {
      const processingIds = videoReviewsRef.current
        .filter((v) => v.status && ACTIVE_PROCESSING_STATUSES.includes(v.status))
        .map((v) => v.id)

      processingIds.forEach((id) => {
        syncProcessingVideo(id).catch((error) => {
          // eslint-disable-next-line no-console
          console.error(`Error polling video review ${id} status:`, error)
        })
      })
    }

    const hasProcessing = videoReviews.some((v) => v.status && ACTIVE_PROCESSING_STATUSES.includes(v.status))
    if (!hasProcessing) {
      return undefined
    }

    poll()
    const intervalId = window.setInterval(poll, PROCESSING_POLL_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [videoReviews, syncProcessingVideo])

  return { syncProcessingVideo }
}
